import { useEffect, useRef } from 'react';
import { toast, Id } from 'react-toastify';
import { normalizeProcessStatus, SendBookingStatus } from '../../../api/socketStatusModel';

export const useBookingToast = (
    status: SendBookingStatus | string | undefined, 
    ExternalActivityId: string | null | undefined, 
    lastError?: string
) => {
    const toastId = useRef<Id | null>(null);


    useEffect(() => {
        const normalizedStatus = normalizeProcessStatus(status);
        if (!normalizedStatus || !ExternalActivityId) return;

        const customId = `booking-${ExternalActivityId}`;

        // Creazione del Toast in fase di PROCESSING (inizio processo)
        if (normalizedStatus === SendBookingStatus.PROCESSING) {
            if (!toast.isActive(customId)) {
                toastId.current = toast.info(`⏳ Pratica ${ExternalActivityId}: In elaborazione...`, {
                    toastId: customId,
                    autoClose: false,
                });
            }
            return;
        }

        // Se non abbiamo un toastId attivo, non procediamo con gli update
        if (!toastId.current) return;

        switch (normalizedStatus) {
            case SendBookingStatus.TICKET_SAVED:
                toast.update(toastId.current, {
                    render: '💾 Dati salvati. Preparazione invio email...',
                    type: 'info'
                });
                break;
            case SendBookingStatus.TICKET_DOWNLOAD_STARTED:
                toast.update(toastId.current, {
                    render: '🎫 Preparazione download ticket...',
                    type: 'info'
                });
                break;
            case SendBookingStatus.TICKET_DOWNLOADING:
            case SendBookingStatus.DOWNLOAD_STARTED:
                toast.update(toastId.current, {
                    render: '⬇️ Download ticket in corso...',
                    type: 'info'
                });
                break;
            case SendBookingStatus.TICKET_DOWNLOAD_COMPLETED:
            case SendBookingStatus.DOWNLOAD_COMPLETED:
                toast.update(toastId.current, {
                    render: '✅ Download ticket completato',
                    type: 'info'
                });
                break;
            case SendBookingStatus.AUDIO_GUIDE_DOWNLOAD_STARTED:
                toast.update(toastId.current, {
                    render: '🎧 Preparazione download audioguida...',
                    type: 'info'
                });
                break;
            case SendBookingStatus.AUDIO_GUIDE_DOWNLOADING:
                toast.update(toastId.current, {
                    render: '⬇️ Download audioguida in corso...',
                    type: 'info'
                });
                break;
            case SendBookingStatus.AUDIO_GUIDE_DOWNLOAD_COMPLETED:
                toast.update(toastId.current, {
                    render: '✅ Download audioguida completato',
                    type: 'info'
                });
                break;
            case SendBookingStatus.SENDING_EMAIL:
                toast.update(toastId.current, {
                    render: '📧 Inizio invio mail...',
                    type: 'info'
                });
                break;
            case SendBookingStatus.EMAIL_SENT:
                toast.update(toastId.current, {
                    render: '✅ Email inviata con successo!',
                    type: 'success',
                    autoClose: 5000
                });
                break;
            case SendBookingStatus.COMPLETED:
                toast.update(toastId.current, {
                    render: '✅ Operazione completata con successo!',
                    type: 'success',
                    autoClose: 5000
                });
                toastId.current = null; // Reset
                break;
            case SendBookingStatus.FAILED:
            case SendBookingStatus.ERROR:
            case SendBookingStatus.TICKET_ERROR:
            case SendBookingStatus.AUDIO_GUIDE_DOWNLOAD_FAILED:
                toast.update(toastId.current, {
                    render: `❌ Errore: ${lastError || 'Processo fallito'}`,
                    type: 'error',
                    autoClose: false
                });
                toastId.current = null; // Reset per permettere un nuovo tentativo
                break;
        }
    }, [status, ExternalActivityId, lastError]);
};